import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, Key, Smartphone, Globe, Monitor, Tablet,
  CheckCircle, AlertTriangle, MapPin, Calendar,
  Loader2, LogOut, ChevronRight, Activity, History
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { formatTimeAgo, getScoreColor } from './securityMockData';

const DeviceIcon = ({ type }) => {
  switch (type) {
    case 'mobile': return <Smartphone className="w-5 h-5" />;
    case 'tablet': return <Tablet className="w-5 h-5" />;
    default: return <Monitor className="w-5 h-5" />;
  }
};

const SecurityOverviewTab = ({
  securityData,
  securityScore,
  activeSessions,
  loginHistory,
  terminatingSession,
  onTerminateSession,
  onTerminateAll,
  user
}) => {
  const navigate = useNavigate();

  return (
    <>
      {/* Security Score & Quick Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <Card className={`border-2 ${getScoreColor(securityScore)}`}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">{securityScore}</div>
              <div className="text-sm font-medium">Security Score</div>
              <div className="mt-3">
                {securityScore >= 80 ? (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                    <CheckCircle className="w-3 h-3" /> Excellent
                  </span>
                ) : securityScore >= 60 ? (
                  <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                    <AlertTriangle className="w-3 h-3" /> Good
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                    <AlertTriangle className="w-3 h-3" /> Needs Attention
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-orange-100 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/mfa')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Key className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-gray-500">MFA Status</span>
                </div>
                <div className={`text-lg font-bold ${securityData?.mfa_enabled ? 'text-green-600' : 'text-amber-600'}`}>
                  {securityData?.mfa_enabled ? 'Enabled' : 'Disabled'}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-orange-100">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-gray-500">Password Changed</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {securityData?.password_last_changed
                ? formatTimeAgo(securityData.password_last_changed)
                : '30 days ago'}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Strength: <span className="text-green-600 capitalize">{securityData?.password_strength || 'Strong'}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-orange-100">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-gray-500">Active Sessions</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {activeSessions.length}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {securityData?.total_logins_30d || 15} logins in 30 days
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Active Sessions */}
        <Card className="border border-orange-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-orange-500" />
                Active Sessions
              </CardTitle>
              {activeSessions.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onTerminateAll}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Logout All Others
                </Button>
              )}
            </div>
            <CardDescription>Devices currently logged into your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeSessions.map((session) => (
                <div
                  key={session.id}
                  className={`p-4 rounded-xl border ${session.is_current ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${session.is_current ? 'bg-orange-100' : 'bg-gray-200'}`}>
                        <DeviceIcon type={session.device_type} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          {session.device}
                          {session.is_current && (
                            <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                          <MapPin className="w-3 h-3" />
                          {session.location}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          IP: {session.ip_address} - Last active: {formatTimeAgo(session.last_active)}
                        </div>
                      </div>
                    </div>
                    {!session.is_current && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onTerminateSession(session.id)}
                        disabled={terminatingSession === session.id}
                        className="text-red-600 hover:bg-red-50"
                      >
                        {terminatingSession === session.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <LogOut className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Login History */}
        <Card className="border border-orange-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-orange-500" />
              Login History
            </CardTitle>
            <CardDescription>Recent login attempts to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {loginHistory.map((login) => (
                <div
                  key={login.id}
                  className={`p-3 rounded-lg border flex items-center justify-between ${
                    login.status === 'success' ? 'bg-white border-gray-100' : 'bg-red-50 border-red-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      login.status === 'success' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {login.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <span className="w-4 h-4 text-red-600 flex items-center justify-center font-bold text-xs">X</span>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        {login.device}
                        {login.is_current && (
                          <span className="text-xs text-orange-500">(Current)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        {login.location} - {login.ip_address}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-medium ${
                      login.status === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {login.status === 'success' ? 'Success' : 'Failed'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {formatTimeAgo(login.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button variant="outline" className="w-full mt-4 border-orange-200 hover:bg-orange-50">
              View Full History
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Security Recommendations */}
      <Card className="mt-8 border border-orange-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            Security Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {!securityData?.mfa_enabled && (
              <div
                onClick={() => navigate('/settings/mfa')}
                className="p-4 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100 transition"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Key className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800">Enable MFA</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Add an extra layer of security with two-factor authentication.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Lock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800">Strong Password</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Use a unique password with letters, numbers, and symbols.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-800">Review Sessions</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Regularly check and terminate unknown sessions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <div className="mt-8 p-6 bg-white rounded-xl border border-orange-100">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-orange-500" />
          Account Information
        </h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Account Created</span>
            <p className="font-medium text-gray-900">
              {securityData?.account_created
                ? new Date(securityData.account_created).toLocaleDateString()
                : '90 days ago'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Email</span>
            <p className="font-medium text-gray-900">{user?.email}</p>
          </div>
          <div>
            <span className="text-gray-500">Role</span>
            <p className="font-medium text-gray-900 capitalize">{user?.role || 'User'}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SecurityOverviewTab;
